import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Typography from '@mui/joy/Typography'
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import {
    getCheckoutFallbackPath,
    getCheckoutFlow,
    getCheckoutReservationToken,
    getCheckoutSuccessPath,
    getValidatedCheckoutReturnTo,
    normalizeCheckoutLocale,
} from '@/checkout-paths'
import ReservationExpiryChip from '@/components/checkout/order-summary/reservation-expiry-chip'
import { CheckoutExpiredState } from '@/components/checkout/page/states'
import { useCreateStripeCheckoutSession } from '@/hooks/api/use-reservation-checkout'
import { ROUTE_SEGMENTS } from '@/pwa/constants'
import { setStoredReservationToken } from '@/session-storage'
import { resolveStripeCheckoutAction } from '@/stripe-checkout'

const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY)

function getPaymentExpiryAt(reservationExpiredAt?: string | null): string | null {
    if (!reservationExpiredAt) {
        return null
    }

    const reservationExpiry = dayjs(reservationExpiredAt)

    if (!reservationExpiry.isValid()) {
        return null
    }

    return reservationExpiry.subtract(1, 'minute').toISOString()
}

export default function CheckoutPaymentView({
    onBack,
    isPwa = false,
}: {
    onBack?: () => void,
    isPwa?: boolean,
}) {
    const { t, i18n } = useTranslation()
    const location = useLocation()
    const navigate = useNavigate()
    const paymentIdRef = useRef<number | null>(null)
    const [checkoutError, setCheckoutError] = useState<string | null>(null)
    const [paymentExpiryAt, setPaymentExpiryAt] = useState<string | null>(null)
    const [reservationExpired, setReservationExpired] = useState(false)
    const setCheckoutRequestInProgress = (_inProgress: boolean) => {}
    const checkoutFlow = getCheckoutFlow(location.search)
    const reservationToken = getCheckoutReservationToken(location.search)
    const returnTo = getValidatedCheckoutReturnTo(location.search)
    const locale = normalizeCheckoutLocale(i18n.resolvedLanguage || i18n.language)
    const garageListPath = isPwa ? `/pwa/${ROUTE_SEGMENTS.search}` : getCheckoutFallbackPath(locale)

    const createStripeCheckoutSession = useCreateStripeCheckoutSession({
        setRequestInProgress: setCheckoutRequestInProgress,
        onErrorCallback: message => {
            setCheckoutError(message)
        },
        onSuccessCallback: () => {
            setCheckoutError(null)
        },
    })
    const stripeCheckoutSessionMutationRef = useRef(createStripeCheckoutSession)
    stripeCheckoutSessionMutationRef.current = createStripeCheckoutSession

    const fetchClientSecret = useCallback(async () => {
        if (reservationToken) {
            setStoredReservationToken(reservationToken)
        }

        const response = await stripeCheckoutSessionMutationRef.current.mutateAsync({
            checkoutUiMode: 'embedded',
            checkoutFlow,
            returnTo,
        })
        paymentIdRef.current = response.data.payment_id
        const nextPaymentExpiryAt = getPaymentExpiryAt(response.data.reservation_expired_at)
        setPaymentExpiryAt(nextPaymentExpiryAt)
        setReservationExpired(Boolean(
            nextPaymentExpiryAt && dayjs(nextPaymentExpiryAt).valueOf() <= Date.now(),
        ))

        const action = resolveStripeCheckoutAction(response.data)
        if (action.type === 'embedded') {
            return action.clientSecret
        }

        setCheckoutError(t('checkout.paymentResult.genericError'))
        throw new Error(t('checkout.paymentResult.genericError'))
    }, [checkoutFlow, reservationToken, returnTo, t])

    const handleCheckoutComplete = useCallback(() => {
        if (!paymentIdRef.current) {
            setCheckoutError(t('checkout.paymentResult.missingPaymentId'))
            return
        }

        navigate(getCheckoutSuccessPath({
            isPwa,
            locale: i18n.resolvedLanguage || i18n.language,
            paymentId: paymentIdRef.current,
            checkoutFlow,
            returnTo,
        }), { replace: true })
    }, [checkoutFlow, i18n.language, i18n.resolvedLanguage, isPwa, navigate, returnTo, t])

    const options = useMemo(() => ({
        fetchClientSecret,
        onComplete: handleCheckoutComplete,
    }), [fetchClientSecret, handleCheckoutComplete])

    useEffect(() => {
        if (!paymentExpiryAt) {
            return
        }

        const expiryTimestamp = dayjs(paymentExpiryAt).valueOf()

        if (Number.isNaN(expiryTimestamp)) {
            return
        }

        const expiryDelay = expiryTimestamp - Date.now()

        if (expiryDelay <= 0) {
            setReservationExpired(true)
            return
        }

        const timeoutId = window.setTimeout(() => {
            setReservationExpired(true)
        }, expiryDelay)

        return () => {
            window.clearTimeout(timeoutId)
        }
    }, [paymentExpiryAt])

    if (reservationExpired) {
        return (
            <CheckoutExpiredState
                title={t('checkout.errors.expiredTitle')}
                text={t('checkout.errors.expiredText')}
                action={(
                    <Button
                        component={Link}
                        to={garageListPath}
                        variant="solid"
                        sx={{
                            fontSize: '15px',
                            padding: '0.875rem 1.25rem',
                            borderRadius: '7px',
                            backgroundColor: 'customBlack.main',
                            mt: 1,
                        }}
                    >
                        {t('checkout.errors.backToGarageList')}
                    </Button>
                )}
            />
        )
    }

    if (checkoutError) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 2,
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: 420,
                        backgroundColor: 'white',
                        borderRadius: '18px',
                        p: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1.5,
                        textAlign: 'center',
                    }}
                >
                    <ErrorOutlineRoundedIcon />
                    <Typography>{checkoutError}</Typography>
                    <Button
                        variant="solid"
                        onClick={onBack}
                        sx={{
                            fontSize: '15px',
                            padding: '0.875rem 1.25rem',
                            borderRadius: '7px',
                            backgroundColor: 'customBlack.main',
                        }}
                    >
                        {t('checkout.paymentResult.backToCheckout')}
                    </Button>
                </Box>
            </Box>
        )
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    maxWidth: '920px',
                    mx: 'auto',
                    px: 2,
                    pt: 5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                }}
            >
                <Button
                    variant="plain"
                    startDecorator={<ArrowBackIosNewRoundedIcon />}
                    onClick={onBack}
                    sx={{
                        color: 'black',
                        '&& svg': {
                            fill: 'currentColor',
                        },
                        '&:hover': {
                            color: '#484848',
                        },
                        '&&:hover svg': {
                            fill: 'currentColor',
                        },
                    }}
                >
                    {t('checkout.actions.back')}
                </Button>
                <ReservationExpiryChip expiredAt={paymentExpiryAt} size="md" />
            </Box>
            <EmbeddedCheckoutProvider
                stripe={stripePromise}
                options={options}
            >
                <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
        </Box>
    )
}
